import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, coverThumbUrl } from "@/lib/appresse";
import { useReader } from "@/components/Layout";

const ResultRow = ({ e, onPick }) => (
  <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={() => onPick(e)}
    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-secondary transition-colors" data-testid={`search-result-${e.id}`}>
    {e.has_cover
      ? <img src={coverThumbUrl(e.id)} alt="" loading="lazy" className="w-9 h-12 object-cover rounded shadow-sm shrink-0" />
      : <div className="w-9 h-12 rounded bg-secondary shrink-0" />}
    <div className="min-w-0">
      <p className="text-sm font-medium truncate">{e.title}</p>
      <p className="text-xs text-muted-foreground">{e.category}</p>
    </div>
  </button>
);

export const SearchDropdown = () => {
  const navigate = useNavigate();
  const { openReader } = useReader();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqRef = useRef(0);

  const term = q.trim();
  useEffect(() => {
    if (!open || !term) { setItems([]); return; }
    const id = ++reqRef.current;
    setLoading(true);
    const t = setTimeout(() => {
      api.editions({ search: term, limit: 8 })
        .then((d) => { if (id === reqRef.current) setItems(d.editions || []); })
        .catch(() => {}).finally(() => { if (id === reqRef.current) setLoading(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [term, open]);

  const goAll = () => { setOpen(false); navigate(`/explorer${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`); };
  const pick = (e) => { setOpen(false); openReader(e); };

  return (
    <form onSubmit={(e) => { e.preventDefault(); goAll(); }} className="relative mt-2" data-testid="home-search-form">
      <button type="submit" aria-label="Rechercher" data-testid="home-search-button"
        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary active:scale-95 transition-colors z-10">
        <Search className="w-5 h-5" />
      </button>
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} data-testid="home-search-input"
        onFocus={() => term && setOpen(true)} onBlur={() => setOpen(false)} onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        autoComplete="off" placeholder="Quelle publication recherchez-vous ?"
        className="pl-11 h-12 rounded-xl bg-card text-base shadow-[0_6px_16px_-4px_rgba(0,0,0,0.4)] hover:shadow-[0_10px_22px_-6px_rgba(0,0,0,0.5)] focus-visible:shadow-[0_10px_22px_-6px_rgba(0,0,0,0.5)] transition-shadow" />
      {open && term && (
        <div className="absolute left-0 right-0 top-full mt-2 z-40 bg-card border border-border rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150" data-testid="home-search-dropdown">
          <div className="max-h-[60vh] overflow-y-auto">
            {loading && !items.length ? (
              <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : items.length ? items.map((e) => <ResultRow key={e.id} e={e} onPick={pick} />) : (
              <p className="px-3 py-4 text-sm text-muted-foreground" data-testid="home-search-empty">Aucune revue trouvée</p>
            )}
          </div>
          <button type="button" onMouseDown={(ev) => ev.preventDefault()} onClick={goAll} data-testid="home-search-all"
            className="w-full flex items-center justify-center gap-1.5 border-t border-border px-3 py-2.5 text-sm text-primary font-medium hover:bg-secondary transition-colors">
            Voir toutes les revues <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </form>
  );
};
